import { useContext, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { useIntl } from "../../contexts/IntlContext";
import LoadingButton from "../ui/LoadingButton";
import { isAuthOwner } from "../../utils/authRole";
import {
  getYoutubeOwnerConnectionStatus,
  YOUTUBE_RECONNECT_PROMPT_SESSION_KEY,
} from "../../services/youtubeOwnerConnection";

const LIVE_STREAMS_PATH = "/transmissoes-ao-vivo";

/** Alerta o proprietário, uma vez por sessão, quando a conexão YouTube precisa ser renovada. */
export function YoutubeReconnectPrompt() {
  const { authInfo, isAuthResolved } = useContext(AuthContext);
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(false);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (!isAuthResolved || !authInfo) return;
    if (!isAuthOwner(authInfo.role)) return;
    if (checkedRef.current) return;

    let alreadyShown = false;
    try {
      alreadyShown = sessionStorage.getItem(YOUTUBE_RECONNECT_PROMPT_SESSION_KEY) === "1";
    } catch {
      // ignore
    }
    if (alreadyShown) return;
    checkedRef.current = true;

    (async () => {
      try {
        const status = await getYoutubeOwnerConnectionStatus();
        if (status.requiresReconnect) {
          setOpen(true);
        }
      } catch {
        // checagem silenciosa: não deve bloquear o restante do app
      } finally {
        try {
          sessionStorage.setItem(YOUTUBE_RECONNECT_PROMPT_SESSION_KEY, "1");
        } catch {
          // ignore
        }
      }
    })();
  }, [authInfo, isAuthResolved]);

  const handleClose = () => setOpen(false);

  const handleReconnect = () => {
    setOpen(false);
    if (location.pathname !== LIVE_STREAMS_PATH) {
      navigate(LIVE_STREAMS_PATH);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth={fullScreen} maxWidth="xs">
      <DialogTitle>{intl.formatMessage({ id: "youtubeConnection.reconnectPromptTitle" })}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ wordBreak: "break-word" }}>
          {intl.formatMessage({ id: "youtubeConnection.reconnectPromptMessage" })}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1, flexWrap: "wrap", gap: 1 }}>
        <LoadingButton onClick={handleClose} variant="text" fullWidth={false}>
          {intl.formatMessage({ id: "youtubeConnection.reconnectPromptLater" })}
        </LoadingButton>
        <LoadingButton onClick={handleReconnect} variant="contained" color="warning" fullWidth={false}>
          {intl.formatMessage({ id: "youtubeConnection.reconnectPromptCta" })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default YoutubeReconnectPrompt;
